import { AngularPlugin } from '@microsoft/applicationinsights-angularplugin-js';
import { ApplicationInsights, ICustomProperties, IDependencyTelemetry, IEventTelemetry, IExceptionTelemetry, IMetricTelemetry, IPageViewTelemetry, ITraceTelemetry, SeverityLevel } from '@microsoft/applicationinsights-web';
import { environment } from '../../environments/environment';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from '../auth.service';
import { filter } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class LoggingService {
  private readonly angularPlugin = new AngularPlugin();
  private consoleErrorPatched = false;
  private globalErrorsPatched = false;
  appInsights: ApplicationInsights;

  constructor(private router: Router, private authService: AuthService) {
    this.appInsights = new ApplicationInsights({
      config: {
        instrumentationKey: environment.appInsights.instrumentationKey,
        enableAutoRouteTracking: true,
        autoTrackPageVisitTime: true,
        enableAjaxErrorStatusText: true,
        enableAjaxPerfTracking: true,
        enableRequestHeaderTracking: true,
        enableResponseHeaderTracking: true,
        maxAjaxCallsPerView: 5000,
        extensions: [this.angularPlugin],
        extensionConfig: {
          [this.angularPlugin.identifier]: { router: this.router },
        },
      },
    });

    this.appInsights.loadAppInsights();
    this.patchConsoleError();
    this.patchGlobalErrors();
    this.initializeUserContext();
  }

  private patchConsoleError(): void {
    if (this.consoleErrorPatched) return;
    this.consoleErrorPatched = true;

    const original = console.error.bind(console);

    console.error = (...args: any[]) => {
      original(...args);
      try {
        const err = args.find(a => a instanceof Error);
        if (err) {
          this.appInsights.trackException({ exception: err }, { source: 'console.error' });
        } else {
          this.appInsights.trackTrace(
            { message: args.map(a => String(a)).join(' '), severityLevel: SeverityLevel.Warning },
            { source: 'console.error' }
          );
        }
      } catch {
      }
    };
  }

  private patchGlobalErrors(): void {
    if (this.globalErrorsPatched) return;
    this.globalErrorsPatched = true;

    globalThis.addEventListener(
      'error',
      (event: Event) => {
        if (!(event.target instanceof HTMLElement)) return;
        try {
          const target = event.target;
          const url =
            (target as HTMLImageElement | HTMLScriptElement).src ||
            (target as HTMLLinkElement).href ||
            '';
          this.appInsights.trackException(
            { exception: new Error(`Resource load failed: ${url}`) },
            { source: 'resource-error', tagName: target.tagName, url }
          );
        } catch {
        }
      },
      true
    );

    globalThis.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
      try {
        const reason = event.reason;
        if (reason instanceof Error) {
          this.appInsights.trackException({ exception: reason }, { source: 'unhandledrejection' });
        } else {
          this.appInsights.trackTrace(
            { message: String(reason), severityLevel: SeverityLevel.Warning },
            { source: 'unhandledrejection' }
          );
        }
      } catch {
      }
    });
  }

  private initializeUserContext(): void {
    this.authService.authState$
      .pipe(filter(isAuth => isAuth) /* take(1) */)
      .subscribe(isAuthenticated => {
        if (isAuthenticated) {
          this.appInsights.setAuthenticatedUserContext(
            this.authService.claims['sub'],
            this.authService.claims['email'],
            true
          );

          // Add user properties to all telemetry
          this.appInsights.addTelemetryInitializer(envelope => {
            envelope.tags = envelope.tags || [];
            envelope.data = envelope.data || [];

            envelope.data['userId'] = this.authService.claims['sub'];
            envelope.data['userEmail'] = this.authService.claims['email'];
            envelope.data['userName'] = this.authService.claims['name'];
            envelope.data['userRoles'] = [this.authService.userRole];
            envelope.tags['ai.cloud.role'] = environment.appInsights.appName;
          });
        } else {
          this.appInsights.clearAuthenticatedUserContext();
        }
      });
  }

  // expose tracking methods
  trackEvent(event: IEventTelemetry, customProperties?: ICustomProperties) {
    this.appInsights.trackEvent(event, customProperties);
  }

  startTrackEvent(name?: string) {
    this.appInsights.startTrackEvent(name);
  }

  stopTrackEvent(
    name: string,
    properties?: { [p: string]: string },
    measurements?: { [p: string]: number }
  ): any {
    this.appInsights.stopTrackEvent(name, properties, measurements);
  }

  trackPageView(pageView?: IPageViewTelemetry) {
    this.appInsights.trackPageView(pageView);
  }

  startTrackPage(name?: string) {
    this.appInsights.startTrackPage(name);
  }

  stopTrackPage(
    name?: string,
    url?: string,
    properties?: { [name: string]: string },
    measurements?: { [name: string]: number }
  ) {
    this.appInsights.stopTrackPage(name, url, properties, measurements);
  }

  trackMetric(metric: IMetricTelemetry, properties?: ICustomProperties) {
    this.appInsights.trackMetric(metric, properties);
  }

  trackException(exception: IExceptionTelemetry, properties?: ICustomProperties) {
    this.appInsights.trackException(exception, properties);
  }

  trackTrace(message: ITraceTelemetry, properties?: ICustomProperties) {
    this.appInsights.trackTrace(message, properties);
  }

  trackDependencyData(dependency: IDependencyTelemetry) {
    this.appInsights.trackDependencyData(dependency);
  }

  flush() {
    this.appInsights.flush();
  }

  logEvent(name: string, properties?: { [key: string]: any }) {
    this.appInsights.trackEvent({ name: name }, properties);
  }

  logTrace(message: string, properties?: { [key: string]: any }) {
    this.trackTrace({ message: message }, properties);
  }

  logException(exception: Error, severityLevel?: number) {
    this.trackException({ exception: exception, severityLevel: severityLevel });
  }
}
